import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridApi } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { PayrollService } from 'src/app/services/payroll.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';
import { StampWizardModalComponent } from '../stamp-wizard-modal/stamp-wizard-modal.component';

type TabKey = 'all' | 'pending' | 'stamped' | 'error';

interface StatusMeta {
    label: string;
    bg: string;
    color: string;
}

const STATUS_META: { [key: string]: StatusMeta } = {
    stamped: { label: 'Timbrado', bg: '#e3fbe8', color: '#3c7e43' },
    pending: { label: 'Pendiente', bg: '#fef3d4', color: '#cf8515' },
    error: { label: 'Error', bg: '#ffccce', color: '#c5010b' }
};

@Component({
    selector: 'app-payroll-receipts-list',
    templateUrl: './payroll-receipts-list.component.html',
    styleUrls: ['./payroll-receipts-list.component.css']
})
export class PayrollReceiptsListComponent implements OnInit, OnDestroy {
    @ViewChild('stampWizard') stampWizard!: StampWizardModalComponent;

    public rowData: any[] = [];
    stats = { total: 0, stamped: 0, pending: 0, error: 0, net_period_total: 0 };

    activeTab: TabKey = 'all';
    filterSearch = '';
    filterPeriod = '';
    /** true cuando se llega desde una fila de "Administrador de periodos" (?period=fecha), muestra el breadcrumb correspondiente. */
    fromPeriods = false;
    private searchDebounce: any;

    loading = false;
    loadError = false;

    gridContext = { componentParent: this };
    private gridApi!: GridApi;

    columnDefs = [
        {
            field: 'employee', headerName: 'Colaborador', flex: 1.3, sortable: false, filter: false,
            cellRenderer: (params: any) => `<a href="javascript:void(0)" class="payroll-name-link">${this.getEmployeeName(params.data)}</a>`
        },
        {
            field: 'period_start', headerName: 'Periodo de pago', flex: 1.1, sortable: false, filter: false,
            valueGetter: (params: any) => this.formatPeriod(params.data)
        },
        {
            field: 'net_paid', headerName: 'Neto a pagar', flex: 0.9, sortable: false, filter: false,
            valueFormatter: (params: any) => this.formatCurrency(params.value)
        },
        {
            field: 'cfdi_status', headerName: 'Estado CFDI', flex: 0.9, sortable: false, filter: false,
            cellRenderer: (params: any) => this.renderStatusBadge(params.value)
        },
        {
            field: 'certification_datetime', headerName: 'Fecha timbrado', flex: 1, sortable: false, filter: false,
            valueFormatter: (params: any) => params.value ? this.formatDate(params.value) : '--/--/----'
        },
        {
            headerName: 'Acciones', width: 110, sortable: false, filter: false, resizable: false,
            cellRenderer: (params: any) => this.renderActions(params.data)
        }
    ];

    public defaultColDef = {
        sortable: false,
        filter: false,
        resizable: true,
    };

    public noRowsTemplate = '<span style="padding: 24px; color: #6b7280;">No se encontraron recibos de nómina</span>';

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private payrollService: PayrollService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Recibos electrónicos', action);
    }

    ngOnInit(): void {
        const period = this.route.snapshot.queryParamMap.get('period');
        if (period) {
            this.filterPeriod = period;
            this.fromPeriods = true;
        }
        this.fetchReceipts();
    }

    volverAlAdminDePeriodos(): void {
        this.router.navigateByUrl('/payroll-periods');
    }

    ngOnDestroy(): void {
        clearTimeout(this.searchDebounce);
    }

    fetchReceipts(): void {
        this.loading = true;
        this.loadError = false;
        this.spinner.show();
        const params: any = {};
        if (this.filterSearch) params.search = this.filterSearch;
        if (this.filterPeriod) params.period = this.filterPeriod;
        if (this.activeTab !== 'all') params.cfdi_status = this.activeTab;

        this.payrollService.listReceipts(params).subscribe({
            next: (resp: any) => {
                this.rowData = Array.isArray(resp?.data) ? resp.data : [];
                this.stats = resp?.stats || this.stats;
                this.loading = false;
                this.spinner.hide();
                // Ver nota en onGridReady: reforzar los cellRenderer una vez
                // que las filas reales ya están cargadas, después de que
                // Angular termine su propio ciclo de detección de cambios
                // (por eso el setTimeout, ver comentario en onGridReady).
                setTimeout(() => this.gridApi?.setColumnDefs(this.columnDefs));
            },
            error: () => {
                this.rowData = [];
                this.loading = false;
                this.loadError = true;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el listado de recibos de nómina', icon: 'error' });
            }
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        // ag-grid no ejecuta correctamente los cellRenderer de función en el
        // primer render cuando columnDefs llega vía el binding [columnDefs] de
        // Angular; el setTimeout empuja la reaplicación fuera del ciclo de
        // detección de cambios en curso, que si no la revierte de inmediato
        // (bug verificado también en employee-list/payroll-list).
        setTimeout(() => params.api.setColumnDefs(this.columnDefs));
    }

    setTab(tab: TabKey): void {
        this.activeTab = tab;
        this.fetchReceipts();
    }

    onSearchChanged(): void {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => this.fetchReceipts(), 350);
    }

    onFilterChanged(): void {
        this.fetchReceipts();
    }

    onCellClicked(event: any): void {
        const data = event?.data;
        if (!data?.id) return;
        const target = (event?.event?.target as HTMLElement) || null;
        const action = target?.closest?.('[data-action]')?.getAttribute('data-action');

        if (action === 'download') {
            this.downloadPdf(data);
        } else if (action === 'stamp') {
            if (!this.canDo('Timbrar')) return;
            this.stampSingle(data);
        } else if (action === 'view' || !action) {
            this.viewReceipt(data);
        }
    }

    viewReceipt(data: any): void {
        this.router.navigateByUrl(`/employee/${data.employee_id}/payroll/${data.id}`);
    }

    downloadPdf(data: any): void {
        this.spinner.show();
        this.payrollService.downloadPdf(data.id).subscribe({
            next: (blob: Blob) => {
                this.spinner.hide();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `recibo-nomina-${data.payroll_number}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo descargar el recibo', icon: 'error' });
            }
        });
    }

    private stampErrorMessage(error: any): string {
        // ApiService.handleError ya reduce el HttpErrorResponse a un string
        // (extrayendo error.error.errors[0].error cuando el backend responde
        // asi), asi que aqui solo llega ese mensaje.
        return (typeof error === 'string' && error) || 'No se pudo timbrar el recibo';
    }

    stampSingle(data: any): void {
        this.spinner.show();
        this.payrollService.stampBulk({ id: data.id }).subscribe({
            next: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El recibo se timbró correctamente', icon: 'success' });
                this.fetchReceipts();
            },
            error: (error) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: this.stampErrorMessage(error), icon: 'error' });
                this.fetchReceipts();
            }
        });
    }

    /**
     * Abre el asistente de timbrado masivo de 4 pasos (seleccionar recibos,
     * confirmar, ver progreso en vivo) en vez de timbrar todo de golpe sin
     * revision previa.
     */
    openStampWizard(): void {
        if (!this.canDo('Timbrar')) return;
        if (!this.stats.pending) {
            this.utils.alertSwal({ title: 'Sin pendientes', text: 'No hay recibos pendientes por timbrar con los filtros actuales', icon: 'info' });
            return;
        }
        const params: any = {};
        if (this.filterSearch) params.search = this.filterSearch;
        if (this.filterPeriod) params.period = this.filterPeriod;
        this.stampWizard.open(params);
    }

    // ---- KPIs ----
    get stampedPercent(): string { return this.pct(this.stats.stamped); }
    get pendingPercent(): string { return this.pct(this.stats.pending); }
    get errorPercent(): string { return this.pct(this.stats.error); }

    private pct(count: number): string {
        if (!this.stats.total) return '0.0';
        return ((count / this.stats.total) * 100).toFixed(1);
    }

    // ---- Helpers ----
    getEmployeeName(data: any): string {
        const employee = data?.employee;
        if (!employee) return '--';
        const parts = [employee.first_name, employee.last_name].filter(Boolean);
        return parts.join(' ') || '--';
    }

    formatPeriod(data: any): string {
        if (!data) return '--';
        return `${this.formatDate(data.period_start)} al ${this.formatDate(data.period_end)}`;
    }

    formatCurrency(value: any): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }

    renderStatusBadge(value: string): string {
        const meta = STATUS_META[value];
        if (!meta) return '';
        return `<span class="payroll-status-badge" style="background:${meta.bg};color:${meta.color}">${meta.label}</span>`;
    }

    renderActions(data: any): string {
        const iconBase = 'assets/img/icons/';
        const canStamp = this.canDo('Timbrar');
        if (data.cfdi_status === 'stamped') {
            return `
                <img src="${iconBase}icon-eye.svg" class="payroll-action-icon" data-action="view" title="Ver recibo">
                <img src="${iconBase}icon-download-04.svg" class="payroll-action-icon" data-action="download" title="Descargar">
            `;
        }
        if (data.cfdi_status === 'error') {
            return `
                <img src="${iconBase}icon-eye.svg" class="payroll-action-icon" data-action="view" title="Ver recibo">
                ${canStamp ? `<img src="${iconBase}icon-reload-round.svg" class="payroll-action-icon" data-action="stamp" title="Reintentar timbrado">` : ''}
            `;
        }
        return canStamp
            ? `<img src="${iconBase}icon-qr-code-small.svg" class="payroll-action-icon" data-action="stamp" title="Generar timbrado">`
            : '';
    }
}
